/**
 * testWs.ts — the backend WS handshake probe (extracted from the old
 * ApiConnectModal). Opens the socket with the same query params ApiAdapter
 * uses (`?key=…&mode=relay`) and waits briefly for the `{kind:'bundle'}`
 * handshake frame. Never rejects — failures resolve as `{ok:false, message}`
 * for inline display.
 */
export type WsTestResult = { ok: true; label: string } | { ok: false; message: string };

export function testWsConnection(
  url: string,
  apiKey: string,
  relay: boolean,
  timeoutMs = 4000,
): Promise<WsTestResult> {
  return new Promise((resolve) => {
    let settled = false;
    let ws: WebSocket | null = null;
    let timer = 0;
    const finish = (result: WsTestResult): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      resolve(result);
    };
    timer = window.setTimeout(
      () => finish({ ok: false, message: 'Timed out waiting for bundle' }),
      timeoutMs,
    );
    try {
      const qs = new URLSearchParams();
      qs.set('key', apiKey);
      if (relay) qs.set('mode', 'relay');
      const fullUrl = url.includes('?')
        ? `${url}&${qs.toString()}`
        : `${url}?${qs.toString()}`;
      ws = new WebSocket(fullUrl);
    } catch (e) {
      finish({ ok: false, message: e instanceof Error ? e.message : String(e) });
      return;
    }
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data as string);
        if (msg.kind === 'bundle') {
          finish({ ok: true, label: `Connected — ${msg.bundle.label}` });
        } else if (msg.kind === 'error') {
          finish({ ok: false, message: msg.message });
        }
      } catch {
        finish({ ok: false, message: 'Malformed server message' });
      }
    };
    ws.onerror = () => finish({ ok: false, message: 'Socket error' });
    ws.onclose = () => finish({ ok: false, message: 'Connection closed before handshake' });
  });
}
